import type {
  ChannelBindingResponse,
  ChatSessionFileReferenceResponse,
  ChatSessionMessageResponse,
  ChatSessionResponse,
  ChatSessionThoughtTraceSummaryResponse,
  ChatSessionUxCapabilitiesResponse,
} from '@automatick/shared-ng';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ScopeOwnedAsyncRequestTracker,
  type ScopeOwnedAsyncRequest,
} from './scope-owned-async';
import { WebChatMessageRuntime } from './web-chat-message.runtime';
import { WebChatRealtimeRuntime } from './web-chat-realtime.runtime';
import { WebChatStateStore } from './web-chat-state.store';
import { WebChatThoughtTraceRuntime } from './web-chat-thought-trace.runtime';
import {
  MESSAGE_HISTORY_TAKE,
  isCanonicalWebChatIdentifier,
  retainUniqueWebChatResources,
  THOUGHT_TRACE_HISTORY_TAKE,
  WEBCHAT_PLATFORM_CODE,
  WebChatThoughtTraceSummary,
} from './web-chat.models';

export class WebChatWorkspaceUnavailableError extends Error {
  readonly code = 'active-workspace-required';

  constructor() {
    super('An active workspace is required to create a Web Chat binding.');
    this.name = 'WebChatWorkspaceUnavailableError';
  }
}

export interface WebChatSessionHistoryResult {
  readonly capabilities: ChatSessionUxCapabilitiesResponse | null;
  readonly messages: ChatSessionMessageResponse[];
  readonly sessionFiles: ChatSessionFileReferenceResponse[];
  readonly thoughtTraces: WebChatThoughtTraceSummary[];
}

interface SessionRequestOwner {
  readonly request: ScopeOwnedAsyncRequest;
  readonly workspaceId: string;
  readonly bindingId: string;
  readonly resourceId: string;
}

export class WebChatSessionRuntime {
  private readonly sessionCatalogRequests = new ScopeOwnedAsyncRequestTracker();
  private destroyed = false;

  constructor(
    private readonly state: WebChatStateStore,
    private readonly messages: WebChatMessageRuntime,
    private readonly thoughtTraces: WebChatThoughtTraceRuntime,
    private readonly realtimeRuntime: WebChatRealtimeRuntime,
  ) {}

  destroy(): void {
    this.destroyed = true;
    this.sessionCatalogRequests.invalidate();
  }

  resolveBinding(): Observable<ChannelBindingResponse | null> {
    const workspaceId = this.activeWorkspaceId;
    if (!workspaceId) {
      return of(null);
    }

    return this.state.channelClient.listBindings().pipe(
      map((bindings) => {
        if (this.destroyed || this.activeWorkspaceId !== workspaceId) {
          return null;
        }

        const ownedBindings = retainUniqueWebChatResources(
          bindings.filter(
            (binding) =>
              isCanonicalWebChatIdentifier(binding.id) &&
              binding.workspaceId === workspaceId &&
              binding.platformCode === WEBCHAT_PLATFORM_CODE &&
              binding.isEnabled !== false,
          ),
          (binding) => binding.id,
        );
        return ownedBindings[0] ?? null;
      }),
      catchError(() => of(null)),
    );
  }

  createBinding(): Observable<ChannelBindingResponse | null> {
    const workspaceId = this.activeWorkspaceId;
    if (!workspaceId) {
      return throwError(() => new WebChatWorkspaceUnavailableError());
    }

    return this.state.channelClient
      .createBinding({
        body: {
          platformCode: WEBCHAT_PLATFORM_CODE,
          channelAccountId: workspaceId,
          displayName: 'Web Control App',
          executionMode: 'AssistedExecution',
          dmPolicyMode: 'open',
          groupPolicyMode: 'off',
          isEnabled: true,
        },
      })
      .pipe(
        map((binding) => {
          if (
            this.destroyed ||
            this.activeWorkspaceId !== workspaceId ||
            binding.workspaceId !== workspaceId ||
            !isCanonicalWebChatIdentifier(binding.id)
          ) {
            return null;
          }

          return binding;
        }),
      );
  }

  resolveSession(
    binding: ChannelBindingResponse,
  ): Observable<ChatSessionResponse | null> {
    const workspaceId = this.activeWorkspaceId;
    const bindingId = canonicalExactIdentifier(binding.id);
    if (!workspaceId || !bindingId) {
      return of(null);
    }

    const owner = this.beginRequest(
      this.sessionCatalogRequests,
      bindingId,
      bindingId,
    );
    if (!owner) {
      return of(null);
    }

    return this.state.chatClient
      .listSessions({
        query: {
          channelBindingId: bindingId,
          take: 50,
          search: this.state.stateSubject.value.sessionSearchTerm || undefined,
          includeArchived: false,
        },
      })
      .pipe(
        map((sessions) => {
          if (!this.ownsCatalog(owner)) {
            return null;
          }

          const ownedSessions = this.acceptSessionCatalog(owner, sessions);
          this.state.patch({ sessions: ownedSessions });
          return (
            ownedSessions.find((session) => session.isActive) ??
            ownedSessions[0] ??
            null
          );
        }),
        catchError(() => of(null)),
      );
  }

  ensureSessionForOutbound(
    firstMessageContent: string,
    uploadCount: number,
    preferredPlatformAgentId?: string | null,
  ): Observable<ChatSessionResponse | null> {
    const currentSession = this.state.stateSubject.value.session;
    if (currentSession?.sessionId) {
      return of(currentSession);
    }

    const workspaceId = this.activeWorkspaceId;
    const bindingId = canonicalExactIdentifier(
      this.state.stateSubject.value.binding?.id,
    );
    if (!workspaceId || !bindingId) {
      return of(null);
    }

    const owner = this.beginRequest(
      this.sessionCatalogRequests,
      bindingId,
      bindingId,
    );
    if (!owner) {
      return of(null);
    }
    const title = this.messages.buildSessionTitleFromFirstMessage(
      firstMessageContent,
      uploadCount,
    );
    return this.state.chatClient
      .spawnSession({
        channelBindingId: bindingId,
        body: {
          conversationId: this.messages.buildConversationId(),
          threadId: title || undefined,
          projectId:
            this.state.stateSubject.value.selectedProjectId ?? undefined,
          preferredPlatformAgentId:
            preferredPlatformAgentId?.trim() || undefined,
        },
      })
      .pipe(
        map((session) => {
          if (
            !this.ownsCatalog(owner) ||
            !isExactWebChatSessionScope(session, workspaceId, bindingId)
          ) {
            return null;
          }

          this.messages.upsertSession(session, true);
          this.state.patch({
            uxCapabilities:
              session.uxCapabilities ??
              this.state.stateSubject.value.uxCapabilities,
          });
          this.realtimeRuntime.connectLive();
          return session;
        }),
        catchError(() => of(null)),
      );
  }

  loadCapabilitiesAndHistory(
    session: ChatSessionResponse,
  ): Observable<WebChatSessionHistoryResult> {
    const workspaceId = this.activeWorkspaceId;
    const bindingId = canonicalExactIdentifier(
      this.state.stateSubject.value.binding?.id,
    );
    const sessionId = canonicalExactIdentifier(session.sessionId);
    if (
      !workspaceId ||
      !bindingId ||
      !sessionId ||
      this.state.stateSubject.value.session?.sessionId !== sessionId ||
      !isExactWebChatSessionScope(session, workspaceId, bindingId)
    ) {
      return of(emptySessionHistory());
    }

    const capReq = this.state.chatClient
      .getSessionUxCapabilities({ sessionId })
      .pipe(catchError(() => of(null)));

    const msgReq = this.state.chatClient
      .listSessionMessages({
        sessionId,
        query: {
          take: MESSAGE_HISTORY_TAKE,
          search: this.state.stateSubject.value.messageSearchTerm || undefined,
        },
      })
      .pipe(catchError(() => of<ChatSessionMessageResponse[]>([])));

    const fileReq = this.state.chatClient
      .listSessionFiles({
        sessionId,
        query: {
          take: 200,
        },
      })
      .pipe(catchError(() => of<ChatSessionFileReferenceResponse[]>([])));

    const traceReq = this.state.chatClient
      .listSessionThoughtTraces({
        sessionId,
        query: {
          take: THOUGHT_TRACE_HISTORY_TAKE,
        },
      })
      .pipe(catchError(() => of<ChatSessionThoughtTraceSummaryResponse[]>([])));

    return forkJoin({
      capabilities: capReq,
      messages: msgReq,
      sessionFiles: fileReq,
      thoughtTraces: traceReq,
    }).pipe(
      map(({ capabilities, messages, sessionFiles, thoughtTraces }) => ({
        capabilities,
        messages: this.messages.sortMessagesChronologically(
          retainUniqueWebChatResources(
            messages.filter((message) => message.sessionId === sessionId),
            (message) => message.entryId,
          ).reverse(),
        ),
        sessionFiles: this.messages.sortSessionFilesChronologically(
          retainUniqueWebChatResources(
            sessionFiles.filter((file) => file.sessionId === sessionId),
            (file) => file.attachmentId,
          ),
        ),
        thoughtTraces: this.thoughtTraces.sortThoughtTracesChronologically(
          retainUniqueWebChatResources(
            thoughtTraces.filter(
              (trace) =>
                trace.sessionId === sessionId &&
                trace.workspaceId === workspaceId,
            ),
            (trace) => trace.thoughtTraceId,
          )
            .map((trace) =>
              this.thoughtTraces.normalizeThoughtTraceSummary(trace),
            )
            .filter(
              (trace): trace is WebChatThoughtTraceSummary => trace !== null,
            ),
        ),
      })),
    );
  }

  private beginRequest(
    tracker: ScopeOwnedAsyncRequestTracker,
    sessionId: string | null | undefined,
    bindingOverride?: string,
  ): SessionRequestOwner | null {
    const workspaceId = this.activeWorkspaceId;
    const normalizedSessionId = canonicalExactIdentifier(sessionId);
    const bindingId = canonicalExactIdentifier(
      bindingOverride ?? this.state.stateSubject.value.binding?.id,
    );
    if (!workspaceId || !bindingId || !normalizedSessionId) {
      return null;
    }

    return {
      request: tracker.begin(buildScopeKey(workspaceId, normalizedSessionId)),
      workspaceId,
      bindingId,
      resourceId: normalizedSessionId,
    };
  }

  private ownsCatalog(owner: SessionRequestOwner): boolean {
    return (
      this.state.stateSubject.value.binding?.id === owner.resourceId &&
      this.ownsSession(this.sessionCatalogRequests, owner)
    );
  }

  private ownsSession(
    tracker: ScopeOwnedAsyncRequestTracker,
    owner: SessionRequestOwner,
  ): boolean {
    return (
      !this.destroyed &&
      this.activeWorkspaceId === owner.workspaceId &&
      this.state.stateSubject.value.binding?.id === owner.bindingId &&
      tracker.owns(
        owner.request,
        buildScopeKey(owner.workspaceId, owner.resourceId),
      )
    );
  }

  private acceptSessionCatalog(
    owner: SessionRequestOwner,
    sessions: readonly ChatSessionResponse[],
  ): ChatSessionResponse[] {
    return retainExactWebChatSessions(
      sessions,
      owner.workspaceId,
      owner.bindingId,
    );
  }

  private get activeWorkspaceId(): string | null {
    return canonicalIdentifier(
      this.state.appState.getStateSnapshot().activeWorkspaceId,
    );
  }
}

function canonicalIdentifier(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function canonicalExactIdentifier(
  value: string | null | undefined,
): string | null {
  return isCanonicalWebChatIdentifier(value) ? value : null;
}

export function isExactWebChatSessionScope(
  session: ChatSessionResponse,
  workspaceId: string,
  bindingId: string,
): boolean {
  return (
    isCanonicalWebChatIdentifier(session.sessionId) &&
    session.workspaceId === workspaceId &&
    session.channelBindingId === bindingId
  );
}

export function retainExactWebChatSessions(
  sessions: readonly ChatSessionResponse[],
  workspaceId: string,
  bindingId: string,
): ChatSessionResponse[] {
  return retainUniqueWebChatResources(
    sessions.filter((session) =>
      isExactWebChatSessionScope(session, workspaceId, bindingId),
    ),
    (session) => session.sessionId,
  );
}

function buildScopeKey(workspaceId: string, resourceId: string): string {
  return `${workspaceId}\u0000${resourceId}`;
}

function emptySessionHistory(): WebChatSessionHistoryResult {
  return {
    capabilities: null,
    messages: [],
    sessionFiles: [],
    thoughtTraces: [],
  };
}
